// Состояние раздела «Скаут» в адресе: район, день и снимок календаря пролётов,
// сверка с АИС и её набор, отбор отметок по вердикту, рамки и рубежи.
//
// Умолчания в адрес НЕ пишутся: иначе «Поделиться» отдавал бы длинную ссылку,
// в которой ничего не выбрано.
//
// Раздел был двумя режимами («Осмотр» и «Датасет») до 28.10.2026. Их ссылки уже
// разосланы и обязаны открываться: `mode=dataset` включает сверку с АИС, а
// `pic=<номер осмотра>` переводится страницей в день и снимок этого осмотра.

export type DatasetSensor = 'all' | 'optical' | 'radar'

/** Набор разбора сверки; тип живёт здесь, чтобы ссылка не тянула логику календаря. */
export type DatasetSetKey = 'all' | 'missed' | 'noais'

export interface ScoutUrlState {
  region: string
  /** `YYYY-MM`; `null` — последний месяц с пролётами. */
  month: string | null
  /** `YYYY-MM-DD`, сутки UTC. */
  day: string | null
  /** `<source>:<scene_id>`. */
  scene: string | null
  sensor: DatasetSensor
  /** Сверка с АИС — только кабинету с признаком `imagery_dataset`. */
  ais: boolean
  /** Набор сверки: все записи, пропуски прибора, отметки без АИС. */
  set: DatasetSetKey
  /** Выбранная запись «судно × снимок». */
  rec: number | null
  /** Выбранная отметка датасета без АИС. */
  mark: number | null
  /** Отобранные вердикты отметок осмотра; пусто = все. */
  verdicts: string[]
  /** Выбранная отметка осмотра: `<sweep_id>:<local_id>`. */
  selected: string | null
  /** Рамки осмотра видны по умолчанию: без них «искали, не нашли» неотличимо
   *  от «сюда не смотрели». */
  looked: boolean
  eez: boolean
  /** Промысловые районы (приказ № 285) поверх снимка. */
  fd: boolean
  /** Старая ссылка `pic=<номер осмотра>`: страница переводит её в день и снимок. */
  legacyPic: number | null
}

export const SCOUT_DEFAULT: ScoutUrlState = {
  region: 'fareast', month: null, day: null, scene: null, sensor: 'all',
  ais: false, set: 'all', rec: null, mark: null,
  verdicts: [], selected: null, looked: true, eez: false, fd: false, legacyPic: null,
}

/** Ключи раздела, включая отжившие: страница стирает их все и пишет заново.
 *  Сенсор — `dsensor`, отметка датасета — `dmark`: `sensor` и `mark` были
 *  заняты «Осмотром» и в старых ссылках значат другое. */
export const SCOUT_KEYS = [
  'region', 'month', 'day', 'scene', 'dsensor', 'ais', 'dset', 'rec', 'dmark',
  'verdict', 'mark', 'looked', 'eez', 'fd',
  'mode', 'days', 'pic', 'sensor',
] as const

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/
const DAY_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/

function asList(raw: string | null): string[] {
  return (raw ?? '')
    .split(',')
    .map(s => s.trim().toLowerCase())
    .filter(s => s.length > 0)
}

/** Положительное целое из адреса; мусор — `null`, а не ноль. */
function asId(raw: string | null): number | null {
  if (raw == null || raw.trim() === '') return null
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : null
}

export function parseScoutUrl(params: URLSearchParams): ScoutUrlState {
  const region = (params.get('region') ?? '').trim()
  const rawDay = params.get('day') ?? ''
  const day = DAY_RE.test(rawDay) ? rawDay : null
  const rawMonth = params.get('month') ?? ''
  const month = MONTH_RE.test(rawMonth) ? rawMonth : day ? day.slice(0, 7) : null
  const scene = params.get('scene')
  const sensor = params.get('dsensor')
  const set = params.get('dset')
  const selected = params.get('mark')
  return {
    region: /^[a-z_]{2,40}$/.test(region) ? region : SCOUT_DEFAULT.region,
    month,
    day,
    scene: scene && /^[a-z]+:\S+$/.test(scene) ? scene : null,
    sensor: sensor === 'optical' || sensor === 'radar' ? sensor : 'all',
    ais: params.get('ais') === '1' || params.get('mode') === 'dataset',
    set: set === 'missed' || set === 'noais' ? set : 'all',
    rec: asId(params.get('rec')),
    mark: asId(params.get('dmark')),
    verdicts: asList(params.get('verdict')),
    selected: selected && /^\d+:\d+$/.test(selected.trim()) ? selected.trim() : null,
    looked: params.get('looked') !== '0',
    eez: params.get('eez') === '1',
    fd: params.get('fd') === '1',
    legacyPic: asId(params.get('pic')),
  }
}

export function writeScoutUrl(state: ScoutUrlState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.region !== SCOUT_DEFAULT.region) params.set('region', state.region)
  if (state.month) params.set('month', state.month)
  if (state.day) params.set('day', state.day)
  if (state.scene) params.set('scene', state.scene)
  if (state.sensor !== 'all') params.set('dsensor', state.sensor)
  if (state.ais) params.set('ais', '1')
  if (state.ais && state.set !== 'all') params.set('dset', state.set)
  if (state.rec != null) params.set('rec', String(state.rec))
  if (state.mark != null) params.set('dmark', String(state.mark))
  if (state.verdicts.length > 0) params.set('verdict', state.verdicts.join(','))
  if (state.selected) params.set('mark', state.selected)
  if (!state.looked) params.set('looked', '0')
  if (state.eez) params.set('eez', '1')
  if (state.fd) params.set('fd', '1')
  // Номер старого осмотра держится, пока страница не перевела его в снимок.
  if (state.legacyPic != null) params.set('pic', String(state.legacyPic))
  return params
}

/** Ключ отметки осмотра: осмотр плюс её номер ВНУТРИ осмотра. */
export function markKey(sweepId: number, localId: number): string {
  return `${sweepId}:${localId}`
}
